// src/pages/property/PropertyPage.js
import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { usePageMetadata } from '../../context/PageMetadataContext';
import { BASE_URL } from '../../utils/config';
import { apiFetch } from '../../utils/apiClient';
import { PropertyTypeNumericToStringMap, PropertyStatusNumeric } from '../../utils/enums';
import { formatDate } from '../../utils/formatting';
import { formatRange, currencyFormatter, percentFormatterForSummary } from '../../utils/formatting';
import { FaFileAlt } from 'react-icons/fa';

// Import reusable display components
import PropertyImageGallery from '../../components/property/display/PropertyImageGallery';
import PropertySummary from '../../components/property/display/PropertySummary';
import PropertyStats from '../../components/property/display/PropertyStats';
import PropertyDetails from '../../components/property/display/PropertyDetails';
import PropertyFinancials from '../../components/property/display/PropertyFinancials';
import PropertyActions from '../../components/property/display/PropertyActions';
import AIReportGenerator from '../../components/property/AIReportGenerator';
import PurchaseModal from '../../components/payment/PurchaseModal';

// import '@fortawesome/fontawesome-free/css/all.min.css'; // Removed to avoid component-level side-effects
import './PropertyPage.css'; // We'll create this CSS file next

const PropertyPage = ({ isPreviewMode = false, propertyData, onClose, propertyIdForPage }) => {
  const { propertyId: propertyIdFromParams } = useParams();
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();
  const { setHasCustomMetadata } = usePageMetadata();
  const lang = useLanguage();
  const navigate = useNavigate();

  const propertyId = propertyIdForPage || propertyIdFromParams;

  const [property, setProperty] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for modals and AI report
  const [isAIReportVisible, setIsAIReportVisible] = useState(false);
  const [orderIdForReport, setOrderIdForReport] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const viewCountUpdatedRef = useRef(false); // Ref to track if view count was updated

  const statusMap = useMemo(
    () => ({
      [PropertyStatusNumeric.ForSale]: t('propertyStatus.forSale'),
      [PropertyStatusNumeric.UnderContract]: t('propertyStatus.underContract'),
      [PropertyStatusNumeric.Sold]: t('propertyStatus.sold'),
      [PropertyStatusNumeric.Rented]: t('propertyStatus.rented'),
      [PropertyStatusNumeric.ComingSoon]: t('propertyStatus.comingSoon'),
      [PropertyStatusNumeric.OffMarket]: t('propertyStatus.offMarket'),
    }),
    [t]
  );

  useEffect(() => {
    // When this page is active, signal that it provides custom metadata.
    setHasCustomMetadata(true);

    // When the component unmounts, signal that it no longer provides custom metadata,
    // allowing other pages to show the default.
    return () => {
      setHasCustomMetadata(false);
    };
  }, [setHasCustomMetadata]);

  useEffect(() => {
    // If in preview mode, use the provided data directly
    if (isPreviewMode && propertyData) {
      setProperty({
        ...propertyData,
        type: PropertyTypeNumericToStringMap[propertyData.type] || propertyData.type || '',
      });
      setStats(propertyData.stats);
      setLoading(false);
      return; // Skip fetching
    }

    // If not in preview mode, fetch data as usual
    const fetchProperty = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch(`${BASE_URL}/api/properties/property/${propertyId}`);
        if (!response.ok) {
          throw new Error(t('propertyPage.propertyNotFound'));
        }
        const data = await response.json();
        setProperty({
          ...data,
          type: PropertyTypeNumericToStringMap[data.type] || data.type || '',
        });
        setStats(data.stats);
      } catch (err) {
        setError(err.message);
        addNotification(err.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    if (propertyId) {
      fetchProperty();
    }
  }, [propertyId, addNotification, t, isPreviewMode, propertyData]);

  // Effect to handle view count increment
  useEffect(() => {
    // Only run when not in preview mode, we have a valid property, and it hasn't been updated yet.
    if (!isPreviewMode && property && property.propertyID && !viewCountUpdatedRef.current) {
      viewCountUpdatedRef.current = true; // Set the flag immediately to prevent double-sends
      const updateViewCount = async () => {
        try {
          // Do not increment view count for the property owner
          if (user && user.isLoggedIn && user.username === property.sellerUsername) {
            return;
          }

          const url = `${BASE_URL}/api/properties/property/${property.propertyID}/stats`;
          const options = {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ viewCount: 1 }),
          };

          // Use apiFetch for logged-in users (respects token), and standard fetch for anonymous views
          const response = user && user.isLoggedIn ? await apiFetch(url, options, user.token) : await fetch(url, options);

          if (response.ok) {
            const updatedData = await response.json();
            setStats(updatedData.stats); // Update local state with the new stats from the backend
          }
        } catch (error) {
          // We don't notify the user about this error as it's a non-critical background task.
          console.error('Failed to update property view count:', error);
        }
      };

      updateViewCount();
    }
  }, [property, user, isPreviewMode]); // Reruns if the property or user changes

  const handleStatsUpdate = (newStats) => {
    setStats(newStats);
  };

  const handlePurchaseSuccess = (order) => {
    setIsPurchaseModalOpen(false);
    addNotification(t('propertyPage.purchaseSuccess'), 'success');
    if (order && order._id) {
      setOrderIdForReport(order._id);
    }
    setIsAIReportVisible(true);
  };

  const handleClose = () => {
    if (isPreviewMode && onClose) {
      onClose(); // Use the callback if in preview mode
    } else {
      onClose ? onClose() : navigate(-1); // Use onClose if available, otherwise go back.
    }
  };

  // Reusable function to generate titles based on an offset, memoized with useCallback.
  const generateTitle = useCallback(
    (offset) => {
      if (!property) return 'SeegSoog'; // Default title

      const propertyIdNumber = parseInt(property.propertyID, 10);
      if (isNaN(propertyIdNumber)) return 'SeegSoog'; // Handle cases where propertyID is not a number

      // Use modulo to get a number from 0 to 2, then add 1 for keys 'pageTitle_1', 'pageTitle_2', 'pageTitle_3'
      const titleIndex = ((propertyIdNumber + offset) % 3) + 1;
      const titleKey = `propertyPage.pageTitle_${titleIndex}`;

      return t(titleKey, {
        city: property.city,
        state: property.state,
        streetAddress: property.address,
        propertyType: property.type,
        defaultValue: `${property.address}, ${property.city} | SeegSoog`, // Fallback
      });
    },
    [property, t]
  ); // Dependencies for useCallback

  // Memoize titles using the memoized generateTitle function
  const pageTitle = useMemo(() => generateTitle(0), [generateTitle]);
  const pageHeading = useMemo(() => generateTitle(1), [generateTitle]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const metaDescription = useMemo(() => {
    if (!property) return ''; // Return empty string if no property
    return t('propertyPage.metaDescription', {
      beds: property.beds,
      baths: property.baths,
      type: property.type,
      address: `${property.address}, ${property.city}, ${property.state}`,
      price: currencyFormatter(property.price),
    });
  }, [property, t]);

  const structuredData = useMemo(() => {
    if (!property) return null;

    const getAvailability = (status) => {
      switch (status) {
        case PropertyStatusNumeric.ForSale:
          return 'https://schema.org/InStock';
        case PropertyStatusNumeric.UnderContract:
          return 'https://schema.org/LimitedAvailability';
        case PropertyStatusNumeric.Sold:
          return 'https://schema.org/SoldOut';
        default:
          return 'https://schema.org/InStock'; // Fallback for 'Coming Soon', 'Off Market', etc.
      }
    };

    return {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: pageTitle, // Use the new dynamic title
      description: metaDescription,
      image: [property.mainImageURL, ...property.propertyImages.map((img) => img.imageUrl)],
      url: `${window.location.origin}/${lang}/property/${property.propertyID}`,
      offers: { '@type': 'Offer', price: property.price, priceCurrency: 'USD', availability: getAvailability(property.status) },
      address: { '@type': 'PostalAddress', streetAddress: property.address, addressLocality: property.city, addressRegion: property.state, postalCode: property.zip, addressCountry: 'US' },
    };
  }, [property, metaDescription, lang, pageTitle]);

  if (loading) {
    return <div className="property-page-message">{t('propertyPage.loading')}</div>;
  }

  if (error) {
    return (
      <div className="property-page-message error">
        {error}
        <Link to={`/${lang}/map`} className="link-button">
          {t('propertyPage.backToMap')}
        </Link>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  // Financial calculations (similar to PropertyDisplay)
  const rentMin = Number(property.rent?.min) || 0;
  const rentMax = Number(property.rent?.max) || (rentMin > 0 ? rentMin : 0);
  const monthlyManagementFeeAmount = {
    min: rentMin * ((Number(property.managementFee) || 0) / 100),
    max: rentMax * ((Number(property.managementFee) || 0) / 100),
  };
  const monthlyVacancyLossAmount = {
    min: rentMin * ((Number(property.vacancyRate) || 0) / 100),
    max: rentMax * ((Number(property.vacancyRate) || 0) / 100),
  };
  const baseMonthlyExpenses =
    (Number(property.hoa) || 0) + (Number(property.propertyTax) || 0) / 12 + (Number(property.insurance) || 0) / 12 + (Number(property.maintenance) || 0) / 12 + (Number(property.utilities) || 0) + (Number(property.otherExpenses) || 0);
  const totalMonthlyExpenses = {
    min: baseMonthlyExpenses + monthlyManagementFeeAmount.min + monthlyVacancyLossAmount.min,
    max: baseMonthlyExpenses + monthlyManagementFeeAmount.max + monthlyVacancyLossAmount.max,
  };
  const totalInitialCosts = (Number(property.price) || 0) + (Number(property.inspectionCost) || 0) + (Number(property.closingCosts) || 0) + (Number(property.additionalCosts) || 0);

  const financialsData = {
    price: property.price,
    inspectionCost: property.inspectionCost,
    closingCosts: property.closingCost,
    additionalCosts: property.additionalCosts,
    additionalCostsDescription: property.additionalCostsDescription,
    rent: formatRange(property.rent?.min, property.rent?.max, currencyFormatter),
    hoa: property.hoa,
    propertyTax: property.propertyTax,
    insurance: property.insurance,
    managementFee: property.managementFee,
    maintenance: property.maintenance,
    utilities: property.utilities,
    vacancyRate: property.vacancyRate,
    otherExpenses: property.otherExpenses,
    otherExpensesDescription: property.otherExpensesDescription,
  };

  const handleGetReportClick = () => {
    if (!user || !user.isLoggedIn) {
      addNotification(t('propertyPage.loginToGetReport'), 'info', null, true, [
        {
          text: t('login.login', 'Login'),
          onClick: () => navigate(`/${lang}/login`),
        },
      ]);
      return;
    }

    //    console.log('User object before report language check:', user);
    if (!user.preferences?.reportLanguage) {
      addNotification(t('propertyPage.setReportLanguage'), 'warning', null, true, [
        {
          text: t('propertyPage.setPreferencesAction', 'Set Preferences'),
          onClick: () => navigate(`/${lang}/preferences`),
        },
      ]);
      return;
    }

    setIsPurchaseModalOpen(true);
  };

  const calculations = {
    totalInitialCosts,
    monthlyManagementFeeAmount: formatRange(monthlyManagementFeeAmount.min, monthlyManagementFeeAmount.max, currencyFormatter, t('formatters.perMonth')),
    monthlyVacancyLossAmount: formatRange(monthlyVacancyLossAmount.min, monthlyVacancyLossAmount.max, currencyFormatter, t('formatters.perMonth')),
    totalMonthlyExpenses: formatRange(totalMonthlyExpenses.min, totalMonthlyExpenses.max, currencyFormatter, t('formatters.perMonth')),
    totalYearlyExpenses: formatRange(totalMonthlyExpenses.min * 12, totalMonthlyExpenses.max * 12, currencyFormatter, t('formatters.perYear')),
    monthlyNetProfit: formatRange(rentMin - totalMonthlyExpenses.max, rentMax - totalMonthlyExpenses.min, currencyFormatter, t('formatters.perMonth')),
    yearlyNetProfit: formatRange((rentMin - totalMonthlyExpenses.max) * 12, (rentMax - totalMonthlyExpenses.min) * 12, currencyFormatter, t('formatters.perYear')),
    displayRoi: formatRange(property.roi?.min, property.roi?.max, percentFormatterForSummary),
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
      <div className="property-page-container">
        <div className="property-page-content">
          <div style={{ textAlign: 'center' }}>
            <h1 className="property-page-heading">{pageHeading}</h1>
          </div>
          <div className="property-page-details-wrapper">
            <PropertyImageGallery property={property} mainImageURL={property.mainImageURL} propertyImages={property.propertyImages} />
            <PropertySummary
              price={property.price}
              address={property.address}
              city={property.city}
              state={property.state}
              zip={property.zip}
              roi={formatRange(property.roi?.min, property.roi?.max, percentFormatterForSummary)}
              beds={property.beds}
              baths={property.baths}
              size={property.size}
            />
            <div className="section-spacing">
              <PropertyStats stats={stats} />
            </div>
            <div className="section-spacing">
              <PropertyDetails
                type={property.type}
                yearBuilt={property.yearBuilt}
                lotSize={property.lotSize}
                tenantsInPlace={property.tenantsInPlace}
                status={property.status}
                price={property.price}
                size={property.size}
                description={property.description}
                statusMap={statusMap}
              />
            </div>
            <div className="section-spacing">
              <PropertyFinancials financialsData={financialsData} calculations={calculations} />
            </div>
            <div className="section-spacing">
              <PropertyActions property={property} onStatsUpdate={handleStatsUpdate} />
              {property.createdOn && (
                <div className="property-meta-display">
                  <span className="created-on-display">
                    {t('propertyPage.createdOn')}: {formatDate(property.createdOn)}
                  </span>
                </div>
              )}
              <div className="ai-report-section section-spacing">
                {isAIReportVisible ? (
                  <AIReportGenerator propertyId={property.propertyID} orderId={orderIdForReport} fullAddress={`${property.address}, ${property.city}, ${property.state} ${property.zip}`} />
                ) : (
                  <div className="ai-report-generator">
                    <h3>{t('propertyPage.aiReportTitle')}</h3>
                    <p>{t('propertyPage.aiReportDescription')}</p>
                    {(!user || !user.isLoggedIn) && (
                      <p>
                        <Trans i18nKey="propertyPage.loginToGetReport">
                          Please <Link to={`/${lang}/login`}>login</Link> or <Link to={`/${lang}/register`}>register</Link> to get an AI report for this property.
                        </Trans>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {ReactDOM.createPortal(
        <button className="property-page-close-btn" onClick={handleClose} aria-label={t('propertyPage.closeAriaLabel')}>
          {/* The '×' is for desktop, the text is for mobile. CSS will toggle visibility. */}
          <span className="close-btn-icon">×</span>
          <span className="close-btn-text">{onClose ? t('propertyPage.backToList', 'Back to List') : t('propertyPage.backToMap')}</span>
        </button>,
        document.body
      )}
      {!isAIReportVisible &&
        ReactDOM.createPortal(
          <button className="floating-ai-report-btn" onClick={handleGetReportClick}>
            <FaFileAlt /> {t('propertyPage.getAiReport')}
          </button>,
          document.body
        )}
      <PurchaseModal isOpen={isPurchaseModalOpen} onClose={() => setIsPurchaseModalOpen(false)} purchaseType="report" propertyId={property.propertyID} onPurchaseSuccess={handlePurchaseSuccess} reportLanguage={user?.preferences?.reportLanguage} />
    </>
  );
};

export default PropertyPage;
